import {
  computePermissions,
  hasPermission,
  isThread,
  Permissions,
  type ChannelDto,
  type MemberDto,
  type PresenceDto,
  type RoleDto,
} from '@lagerfeuer/shared';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { CustomStatusText } from '../profile/CustomStatusText';
import { activeCustomStatus } from '../profile/customStatus';
import { Avatar, effectiveStatus } from '../../components/Avatar';
import { anchorFrom } from '../../components/Popover';
import { membersApi } from '../../lib/endpoints';
import { qk } from '../../lib/queryClient';
import { onResync } from '../../realtime/bus';
import { gatewaySend, useOnConnected } from '../../realtime/gateway';
import { useAppStore } from '../../store/app';
import { overlays } from '../../store/overlays';
import { displayName, dmParticipants, effectiveOverwrites, memberColor } from '../../store/selectors';

/** Seitengröße der Mitgliederliste (Vertrag: limit ≤ 1000). */
const MEMBER_PAGE_SIZE = 1000;

/** Mitgliederliste (rechts): nach hervorgehobenen Rollen gruppiert, Online vor Offline. */
export function MemberList({ serverId, channelId }: { serverId: string; channelId?: string }) {
  const members = useAppStore((s) => s.members[serverId]);
  const roles = useAppStore((s) => s.roles[serverId]);
  const presences = useAppStore((s) => s.presences);
  const server = useAppStore((s) => s.servers[serverId]);
  const channel = useAppStore((s) => (channelId ? s.channels[channelId] : undefined));
  const parent = useAppStore((s) => (channel?.parentId ? s.channels[channel.parentId] : undefined));

  // Seitenweise (CursorPage, `nextCursor` als `after=`); jede Seite landet im Store.
  const q = useInfiniteQuery({
    queryKey: qk.members(serverId),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const page = await membersApi.list(serverId, { limit: MEMBER_PAGE_SIZE, after: pageParam });
      useAppStore.getState().update((d) => {
        d.members[serverId] = { ...(d.members[serverId] ?? {}) };
        for (const m of page.items) {
          d.members[serverId]![m.user.id] = m;
          d.users[m.user.id] = m.user;
        }
      });
      return { count: page.items.length, nextCursor: page.nextCursor };
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 5 * 60_000,
  });
  const loadMore = () => {
    if (q.hasNextPage && !q.isFetchingNextPage) void q.fetchNextPage();
  };
  const onScroll = (e: React.UIEvent<HTMLElement>) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 400) loadMore();
  };

  useEffect(() => subscribeServer(serverId, channelId), [serverId, channelId]);
  // Nach einer Neuverbindung erneut abonnieren (Präsenzen; Mitglieder lädt die Resync-Invalidierung nach).
  useOnConnected(() => subscribeServer(serverId, channelId));
  // Nach erneutem READY nochmals (frische Präsenzen nach der Trennung).
  useEffect(() => onResync(() => subscribeServer(serverId, channelId)), [serverId, channelId]);

  const groups = useMemo(() => {
    if (!members || !roles || !server) return [];
    const list = Object.values(members);
    const viewChannel = channel ? (isThread(channel.type) ? parent : channel) : undefined;
    const visible = viewChannel ? list.filter((m) => canView(m, roles, server.ownerId, serverId, viewChannel, useAppStore.getState())) : list;
    return groupMembers(visible, roles, presences);
  }, [members, roles, presences, server, channel, parent, serverId]);

  return (
    <aside className="member-list scroll" aria-label="Mitglieder" data-testid="member-list" onScroll={onScroll}>
      {q.isLoading && !members && <div className="empty"><div className="spinner" /></div>}
      {groups.map((g) => (
        <section key={g.id} className="member-group" data-testid="member-group">
          <h3 className="member-group-title">
            {g.name} — {g.members.length}
          </h3>
          <ul role="list">
            {g.members.map((m) => (
              <MemberRow key={m.user.id} member={m} roles={roles ?? []} serverId={serverId} offline={g.id === 'offline'} />
            ))}
          </ul>
        </section>
      ))}
      {q.hasNextPage && (
        <button type="button" className="btn-link member-list-more" onClick={loadMore} disabled={q.isFetchingNextPage} data-testid="member-list-more">
          {q.isFetchingNextPage ? 'Wird geladen …' : 'Weitere Mitglieder laden'}
        </button>
      )}
    </aside>
  );
}

/** Präsenz-Abo des Servers (Vertrag 6.2 SERVER_SUBSCRIBE); übernimmt die gelieferten Präsenzen. */
function subscribeServer(serverId: string, channelId: string | undefined): void {
  void gatewaySend('SERVER_SUBSCRIBE', channelId ? { serverId, channelId } : { serverId }).then((res) => {
    const data = (res as { ok?: boolean; data?: { presences?: PresenceDto[] } } | null)?.data;
    if (data?.presences) {
      useAppStore.getState().update((d) => {
        for (const p of data.presences!) d.presences[p.userId] = p;
      });
    }
  });
}

function canView(m: MemberDto, roles: RoleDto[], ownerId: string, serverId: string, ch: ChannelDto, s: ReturnType<typeof useAppStore.getState>): boolean {
  const ids = new Set([serverId, ...m.roleIds]);
  const perms = computePermissions(
    roles.filter((r) => ids.has(r.id)),
    effectiveOverwrites(s, ch),
    m.user.id === ownerId,
    { everyoneRoleId: serverId, memberId: m.user.id, channelKind: ch.type === 'VOICE' ? 'voice' : 'text' },
  );
  return hasPermission(perms, Permissions.VIEW_CHANNEL);
}

export interface MemberGroup {
  id: string;
  name: string;
  members: MemberDto[];
}

/** Gruppierung: hervorgehobene Rollen (absteigend), dann „Online", dann „Offline". */
export function groupMembers(list: MemberDto[], roles: RoleDto[], presences: Record<string, PresenceDto>): MemberGroup[] {
  const hoisted = roles.filter((r) => r.hoist && !r.isEveryone).sort((a, b) => b.position - a.position);
  const groups = new Map<string, MemberGroup>();
  for (const r of hoisted) groups.set(r.id, { id: r.id, name: r.name, members: [] });
  const online: MemberGroup = { id: 'online', name: 'Online', members: [] };
  const offline: MemberGroup = { id: 'offline', name: 'Offline', members: [] };
  for (const m of list) {
    const st = effectiveStatus(presences[m.user.id]?.status);
    if (st === 'OFFLINE') {
      offline.members.push(m);
      continue;
    }
    const role = hoisted.find((r) => m.roleIds.includes(r.id));
    (role ? groups.get(role.id)! : online).members.push(m);
  }
  const byName = (a: MemberDto, b: MemberDto) => displayName(a.user, a).localeCompare(displayName(b.user, b), 'de');
  const out = [...groups.values(), online, offline].filter((g) => g.members.length > 0);
  for (const g of out) g.members.sort(byName);
  return out;
}

function MemberRow({ member, roles, serverId, offline }: { member: MemberDto; roles: RoleDto[]; serverId: string; offline: boolean }) {
  const presence = useAppStore((s) => s.presences[member.user.id]);
  const color = memberColor(roles, member.roleIds);
  return (
    <li>
      <button
        className={`member-row ${offline ? 'offline' : ''}`}
        onClick={(e) => overlays.profile(member.user.id, anchorFrom(e.currentTarget), serverId)}
        data-testid="member-item"
        data-user-id={member.user.id}
      >
        <Avatar user={member.user} overrideUrl={member.serverAvatarUrl} size={32} showStatus />
        <span className="member-text">
          <span className="truncate member-name" style={color ? { color } : undefined}>
            {displayName(member.user, member)}
            {member.user.isBot && <span className="bot-tag">BOT</span>}
          </span>
          {activeCustomStatus(presence?.customStatus) && !offline && (
            <span className="truncate small faint" data-testid="member-custom-status">
              <CustomStatusText status={presence?.customStatus} emojiSize={14} />
            </span>
          )}
        </span>
      </button>
    </li>
  );
}

/** Teilnehmerliste einer Gruppen-DM. */
export function RecipientList({ channelId }: { channelId: string }) {
  const ch = useAppStore((s) => s.channels[channelId]);
  const me = useAppStore((s) => s.me);
  const presences = useAppStore((s) => s.presences);
  if (!ch) return null;
  const users = dmParticipants(ch, me);
  return (
    <aside className="member-list scroll" aria-label="Mitglieder" data-testid="member-list">
      <section className="member-group">
        <h3 className="member-group-title">Mitglieder — {users.length}</h3>
        <ul role="list">
          {users.map((u) => (
            <li key={u.id}>
              <button className={`member-row ${effectiveStatus(presences[u.id]?.status) === 'OFFLINE' ? 'offline' : ''}`} onClick={(e) => overlays.profile(u.id, anchorFrom(e.currentTarget))} data-testid="member-item" data-user-id={u.id}>
                <Avatar user={u} size={32} showStatus />
                <span className="member-text">
                  <span className="truncate member-name">
                    {displayName(u)}
                    {ch.ownerId === u.id && <span className="tag" title="Gruppenbesitz">👑</span>}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </section>
    </aside>
  );
}
